import React, { useState } from 'react';
import { TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@material-ui/core';
import styled from 'styled-components';
import HealtheraTable from '../../../../../components/Table/Table';
import useDebouncedOrganisationSearch from '../../../CreateClinic/hooks/useOrganisationSearch';
import useExcludedOrganisations from '../../hooks/useExcludedOrganisations';
import { Organisation } from '../../../../../types/organisation';
import { ModifyClinicProps } from '../../../types';
import AddIcon from '@material-ui/icons/Add';
import TableContainer from '../../../../Shared/TableContainer';

interface ClinicExcludedOrganisationsTabProps {
  clinic: ModifyClinicProps['clinic'];
}

const ClinicExcludedOrganisationsTab: React.FC<ClinicExcludedOrganisationsTabProps> = ({ clinic }) => {
  const { searchInput, setSearchInput, organisations } = useDebouncedOrganisationSearch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    organisations: excludedOrganisations,
    totalCount,
    addOrganisation,
    removeOrganisation,
  } = useExcludedOrganisations(clinic._id, page, rowsPerPage);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organisationToDelete, setOrganisationToDelete] = useState<Organisation | null>(null);

  const handleAddOrganisation = async (organisation: Organisation) => {
    await addOrganisation(organisation._id);
    setSearchInput('');
  };

  const handleRemoveOrganisation = async (organisationId: string) => {
    await removeOrganisation(organisationId);
    setDeleteDialogOpen(false);
    setOrganisationToDelete(null);
  };

  const handleDeleteClick = (org: Organisation) => {
    setOrganisationToDelete(org);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setOrganisationToDelete(null);
  };

  const prepareSearchData = (orgs: Organisation[]) => {
    return [
      {
        view: {
          name: 'Name',
        },
        headers: true
      },
      ...orgs.map(org => ({
        view: {
          name: org.name,
        },
        button: {
          title: 'Add',
          onClick: () => handleAddOrganisation(org),
          icon: <AddIcon />
        }
      }))
    ];
  };

  const prepareListingData = (orgs: Organisation[]) => {
    return [
      {
        view: {
          name: 'Name',
        },
        headers: true
      },
      ...orgs.map(org => ({
        view: {
          name: org.name,
        },
        button: {
          title: 'Remove',
          onClick: () => handleDeleteClick(org)
        }
      }))
    ];
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Container>
        <h2>&nbsp;</h2>
        <ExplanationText>
          <Typography variant="body2">
            For these organisations, the Clinic will be hidden from non-referred patients on the Healthera app
          </Typography>
        </ExplanationText>
        <SearchContainer>
          <SearchField
            fullWidth
            label="Search organisations to add"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </SearchContainer>

        {searchInput.length > 2 && (
          <HealtheraTable
            data={prepareSearchData(organisations)}
            displayPagination={false}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            onChangePage={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={organisations.length}
            isLiveDashboard={false}
            noHover={true}
            onRowClick={() => {}}
          />
        )}

        <TableContainer>
          {excludedOrganisations.length > 0 ? (
            <HealtheraTable
              data={prepareListingData(excludedOrganisations)}
              displayPagination
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              onChangePage={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              totalCount={totalCount}
              isLiveDashboard={false}
              noHover={true}
              onRowClick={() => {}}
            />
          ) : (
            <EmptyStateContainer>
              <Typography variant="body1" color="textSecondary">
                No excluded organisations have been added yet. Search for an organisation above to add one
              </Typography>
            </EmptyStateContainer>
          )}
        </TableContainer>

        <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
          <DialogTitle>Remove Excluded Organisation</DialogTitle>
          <DialogContent>
            Are you sure you want to remove {organisationToDelete?.name} from excluded organisations?
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => organisationToDelete && handleRemoveOrganisation(organisationToDelete._id)}
              color="secondary"
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 24px;
`;

const SearchContainer = styled.div`
  padding-bottom: 24px;
`;

const SearchField = styled(TextField)`
  max-width: 50%;
`;

const EmptyStateContainer = styled.div`
  padding: 24px;
  text-align: center;
`;

const ExplanationText = styled.div`
  padding-bottom: 24px;
`;

export default ClinicExcludedOrganisationsTab; 
